// Lightweight formatters used by dashboard widgets. Browser-only.

const MS_PER_MIN = 60_000;
const MS_PER_HOUR = 3_600_000;
const MS_PER_DAY = 86_400_000;

/**
 * Format an ISO datetime as a short relative-time string.
 *
 * - <1 min      → "just now"
 * - <60 min     → "X min ago"
 * - <24 hours   → "X hour" / "X hours ago"
 * - 1 day       → "Yesterday"
 * - 2–7 days    → "X days ago"
 * - older       → "DD MMM" (e.g. "5 Apr")
 *
 * Future dates (clock skew) collapse to "just now".
 */
export function formatRelativeTime(iso: string, now: Date = new Date()): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';

  const diffMs = now.getTime() - date.getTime();
  if (diffMs < MS_PER_MIN) return 'just now';

  const diffMin = Math.floor(diffMs / MS_PER_MIN);
  if (diffMin < 60) return `${diffMin} min ago`;

  const diffHour = Math.floor(diffMs / MS_PER_HOUR);
  if (diffHour < 24) return `${diffHour} hour${diffHour === 1 ? '' : 's'} ago`;

  const diffDay = Math.floor(diffMs / MS_PER_DAY);
  if (diffDay === 1) return 'Yesterday';
  if (diffDay < 7) return `${diffDay} days ago`;

  return date.toLocaleDateString('en-MT', { day: 'numeric', month: 'short' });
}
